import * as React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CheckResult } from "../../models/heretic/checkResult";
import { ProcedureCheck } from "../../models/heretic/procedureCheck";
import { CheckmarkCircle24Filled, DismissCircle24Filled } from "@fluentui/react-icons";
import { fetchFastApi } from "../../utils/fastApi";
import { AccordionItem, Accordion, AccordionHeader, AccordionPanel } from "@fluentui/react-components";
import { evaluatedColumnSchema } from "../../models/heretic/auditSession";
import { z } from "zod";

interface CheckResultCardProps {
  currentChecks: {
    checkResults: CheckResult[];
    procedureChecks: ProcedureCheck[];
  } | null;
  onCellClick?: (sourceDocument: any) => void;
  sourceRowId?: string;
  sessionId?: string;
  onUpdateCheckResult?: (updatedChecks: {
    checkResults: CheckResult[];
    procedureChecks: ProcedureCheck[];
    checkId: string;
  }) => void;
  checkId?: string;
  evalColumnId?: string;
  evalColumn?: z.infer<typeof evaluatedColumnSchema>;
}

export const CheckResultCard: React.FC<CheckResultCardProps> = ({
  currentChecks,
  sourceRowId,
  sessionId,
  onUpdateCheckResult,
  checkId,
  evalColumn,
}) => {
  if (!currentChecks) {
    return null;
  }

  const { checkResults, procedureChecks } = currentChecks;

  // Get the most recent AI review
  const aiReview = React.useMemo(() => {
    return checkResults
      .filter((result) => result.reviewerType === "system")
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  }, [checkResults]);

  // Get the most recent human review
  const humanReview = React.useMemo(() => {
    return checkResults
      .filter((result) => result.reviewerType === "human")
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  }, [checkResults]);

  const procedureCheck = React.useMemo(() => {
    return procedureChecks.find((pc) => pc.id === checkId);
  }, [procedureChecks, checkId]);

  // State for editing
  const [humanDescription, setHumanDescription] = React.useState(humanReview?.reason || "");
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editDescription, setEditDescription] = React.useState("");
  const [editDecision, setEditDecision] = React.useState<"pass" | "fail">("pass");

  // Handler for making a decision (pass/fail)
  const handleDecision = async (decision: "pass" | "fail") => {
    if (!sourceRowId || !sessionId || !onUpdateCheckResult) return;

    setIsUpdating(true);
    try {
      await fetchFastApi({
        method: humanReview ? "PUT" : "POST",
        path: humanReview
          ? `/matching/match-session/${sessionId}/source-row/${sourceRowId}/review/${checkId}/result/${humanReview.id}`
          : `/matching/match-session/${sessionId}/source-row/${sourceRowId}/review/${checkId}`,
        body: {
          decision,
          reason: humanDescription,
        },
      });

      // Create a new review object
      const newReview = {
        id: humanReview?.id || crypto.randomUUID(),
        checkId: checkId,
        reviewerType: "human" as const,
        userId: null,
        decision,
        reason: humanDescription,
        reviewOrder: 1,
        citations: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Update the local state
      const updatedCheckResults = humanReview
        ? checkResults.map((cr) => (cr.id === humanReview.id ? newReview : cr))
        : [...checkResults, newReview];

      // Call the callback to update parent state
      onUpdateCheckResult({
        checkResults: updatedCheckResults,
        procedureChecks,
        checkId,
      });
    } catch (error) {
      console.error("Failed to update check result", error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handler for starting edit
  const handleStartEdit = () => {
    setEditDescription(humanReview?.reason || "");
    setEditDecision(humanReview?.decision || "pass");
    setIsEditing(true);
  };

  // Handler for saving edit
  const handleSaveEdit = async () => {
    if (!humanReview || !sourceRowId || !sessionId || !onUpdateCheckResult) return;

    setIsUpdating(true);
    try {
      await fetchFastApi({
        method: "PUT",
        path: `/matching/match-session/${sessionId}/source-row/${sourceRowId}/review/${checkId}/result/${humanReview.id}`,
        body: {
          decision: editDecision,
          reason: editDescription,
        },
      });

      // Update the local state
      const updatedCheckResults = checkResults.map((cr) =>
        cr.id === humanReview.id
          ? {
              ...cr,
              decision: editDecision,
              reason: editDescription,
              updatedAt: new Date().toISOString(),
            }
          : cr
      );

      // Call the callback to update parent state
      onUpdateCheckResult({
        checkResults: updatedCheckResults,
        procedureChecks,
        checkId,
      });

      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update check result", error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="p-4">
      <div className="border rounded-lg pb-4 space-y-4 w-full">
        <div>
          <div className="flex w-full justify-between items-center px-4 py-2 border-b border-stone-200 bg-stone-100 font-medium rounded-t-lg">
            <div className="">{procedureCheck?.name || evalColumn?.name}</div>
          </div>
          <Accordion collapsible className="mt-2">
            <AccordionItem value="instructions">
              <AccordionHeader className="text-base font-normal">Test Description</AccordionHeader>
              <AccordionPanel className="p-2">
                {procedureCheck?.instructions || evalColumn?.instructions}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </div>
        <div className="p-4 space-y-4">
          {aiReview && (
            <div className="rounded-tr-lg rounded-tl-lg border border-stone-200">
              <div
                className={`p-2 rounded-tr-lg rounded-tl-lg flex flex-shrink-0 items-center gap-2 border-b border-stone-200 ${
                  aiReview.decision === "pass" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                }`}
              >
                {aiReview.decision === "pass" ? (
                  <CheckmarkCircle24Filled className="w-5 h-5" />
                ) : (
                  <DismissCircle24Filled className="w-5 h-5" />
                )}
                <span className="capitalize">AI Test Result: {aiReview.decision}</span>
              </div>
              <div className="flex items-start p-4 bg-slate-100 gap-3">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <img src="/assets/logo_bg_white.svg" className="w-6 h-6" />
                    <span className="font-medium">Midship Notes</span>
                  </div>
                  <div className="mt-1 markdown-body">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{aiReview.reason}</ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
